import { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useI18n } from "../i18n";

export interface InputDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  defaultValue?: string;
  placeholder?: string;
  /** A line under the field: what it accepts. */
  hint?: string;
  /** The field's name, shown to the left of it (the short-form row of the modals brief 28.10). */
  label?: string;
  /** The server's limit for this value: a counter in the field and a refusal before sending, not after. */
  maxLength?: number;
  /** Anything else the server refuses: the reason, or null when the value is fine. */
  validate?: (value: string) => string | null;
  onConfirm: (value: string) => void;
  onCancel?: () => void;
}

export default function InputDialog({
  open,
  onOpenChange,
  title,
  defaultValue = "",
  placeholder,
  hint,
  label,
  maxLength,
  validate,
  onConfirm,
  onCancel,
}: InputDialogProps) {
  const { t } = useI18n();
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setValue(defaultValue);
      // Auto-focus with a small delay so the dialog has rendered
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [open, defaultValue]);

  const tooLong = maxLength !== undefined && value.trim().length > maxLength;
  const error = tooLong
    ? t('inputDialog.tooLong').replace('{max}', String(maxLength))
    : (validate?.(value) ?? null);

  const handleConfirm = useCallback(() => {
    if (error !== null) return;
    onConfirm(value);
    onOpenChange(false);
  }, [value, error, onConfirm, onOpenChange]);

  const handleCancel = useCallback(() => {
    onCancel?.();
    onOpenChange(false);
  }, [onCancel, onOpenChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleConfirm();
      }
      if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
    },
    [handleConfirm, handleCancel],
  );

  const field = (
    <div className="relative min-w-0">
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label={label ?? title}
        aria-invalid={error !== null}
        className={maxLength !== undefined ? "pr-14" : undefined}
        data-testid="input-dialog-input"
      />
      {maxLength !== undefined && (
        <span
          className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-pill tabular-nums ${tooLong ? "text-danger" : "text-dim"}`}
          data-testid="input-dialog-count"
        >
          {value.trim().length}/{maxLength}
        </span>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleCancel(); }}>
      <DialogContent size="md" data-testid="input-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {label !== undefined ? (
          <div className="grid grid-cols-[96px_1fr] items-center gap-2">
            <span className="text-meta text-muted-foreground">{label}</span>
            {field}
          </div>
        ) : field}
        {error !== null && <p className="text-meta text-danger" role="alert" data-testid="input-dialog-error">{error}</p>}
        {hint !== undefined && <p className="text-meta text-muted-foreground" data-testid="input-dialog-hint">{hint}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} data-testid="input-dialog-cancel">
            {t('inputDialog.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={error !== null} data-testid="input-dialog-confirm">
            {t('inputDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
